import React, { useState } from 'react';
import { useForm } from '../../context/FormContext';
import StepLayout from '../StepLayout';

function Step4WebsiteContent() {
  const { formData, updateFormData } = useForm();
  const stepData = formData.step4;
  const industry = formData.step1.industry;
  const [showAllPages, setShowAllPages] = useState(false);
  const [expandedPages, setExpandedPages] = useState({});

  const handlePageToggle = (pageId) => {
    const currentPages = stepData.selectedPages || [];
    const updatedPages = currentPages.includes(pageId)
      ? currentPages.filter(id => id !== pageId)
      : [...currentPages, pageId];
    updateFormData('step4', { selectedPages: updatedPages });
  };

  const handleContentUpdate = (pageId, content) => {
    const currentContent = stepData.pageContent || {};
    updateFormData('step4', { pageContent: { ...currentContent, [pageId]: content } });
  };

  const toggleContentSection = (pageId) => {
    setExpandedPages(prev => ({ ...prev, [pageId]: !prev[pageId] }));
  };

  const getIndustrySuggestions = () => {
    const suggestions = {
      'beauty-hair-wellness': ['Home', 'About Us', 'Services / Treatments', 'Pricing', 'Gallery / Portfolio', 'Booking', 'Contact'],
      'construction-home-improvement': ['Home', 'About Us', 'Services', 'Projects / Portfolio', 'Testimonials', 'Contact'],
      'consulting-professional-services': ['Home', 'About Us / Team', 'Services', 'Case Studies / Clients', 'Blog / Insights', 'Contact'],
      'creative-media': ['Home', 'About Us', 'Services', 'Portfolio / Gallery', 'Testimonials', 'Blog / Insights', 'Contact'],
      'education-training': ['Home', 'About Us', 'Courses / Programmes', 'Admissions / Enrolment', 'Events / Calendar', 'Resources / Blog', 'Contact'],
      'events-entertainment-hospitality': ['Home', 'About Us', 'Services / Packages', 'Portfolio / Past Events', 'Booking / Reservations', 'Testimonials', 'Contact'],
      'farming-agriculture': ['Home', 'About Us', 'Products / Produce', 'Services', 'Sustainability / Practices', 'Contact'],
      'finance-legal': ['Home', 'About Us / Team', 'Services', 'Resources / Blog', 'FAQ', 'Contact'],
      'healthcare-medical': ['Home', 'About Us / Practitioners', 'Services / Treatments', 'Booking / Appointments', 'Patient Resources / FAQ', 'Contact'],
      'landscaping-gardening': ['Home', 'About Us', 'Services', 'Projects / Portfolio', 'Testimonials', 'Contact'],
      'laundry-dry-cleaning': ['Home', 'About Us', 'Services', 'Pricing', 'Booking / Pick-up Request', 'Contact'],
      'manufacturing-industrial': ['Home', 'About Us', 'Products / Catalogue', 'Services', 'Contact'],
      'non-profit-community': ['Home', 'About Us / Our Story', 'Programmes / Projects', 'Get Involved / Volunteer', 'Donate', 'Events / News', 'Contact'],
      'real-estate-property': ['Home', 'About Us / Agents', 'Properties / Listings', 'Services', 'Blog / Market Insights', 'Contact'],
      'restaurant-catering-food': ['Home', 'About Us', 'Menu', 'Reservations / Online Orders', 'Gallery', 'Events / Specials', 'Contact'],
      'retail-ecommerce': ['Home', 'About Us', 'Shop / Products', 'Cart / Checkout', 'Blog / News', 'Contact'],
      'technology-it': ['Home', 'About Us', 'Services', 'Case Studies / Clients', 'Blog / Insights', 'Contact'],
      'transport-logistics': ['Home', 'About Us', 'Services', 'Fleet / Capabilities', 'Contact'],
      'tourism-travel-accommodation': ['Home', 'About Us', 'Destinations / Packages', 'Booking / Reservations', 'Gallery', 'Contact'],
      'other': ['Home', 'About Us', 'Services / Products', 'Contact Us']
    };
    return suggestions[industry] || suggestions['other'];
  };

  const essentialPages = [
    { id: 'home', title: 'Home', description: 'Your main landing page that introduces visitors to your business.', details: 'Include a clear value proposition, headline, engaging visuals, and a strong call-to-action.' },
    { id: 'about', title: 'About Us', description: 'Tells the story of your business and what sets you apart.', details: 'Share your mission, vision, values, history, and any unique qualities that build trust and credibility.' },
    { id: 'services', title: 'Services', description: 'An overview of the services you offer.', details: 'Explain each service clearly, highlighting benefits, features, and the problems you solve for clients.' },
    { id: 'products', title: 'Products', description: 'A catalogue of your products for customers to explore.', details: 'Include descriptions, features, specifications, pricing (optional), images, and categories if applicable.' },
    { id: 'team', title: 'Our Team', description: 'Profiles of your key people, such as leadership and specialists.', details: 'Add photos, roles, and bios to humanise your business and build client confidence.' },
    { id: 'pricing', title: 'Pricing', description: 'Outlines your packages, plans, or service rates.', details: 'Include comparisons, clear value propositions, and calls-to-action to help clients make informed decisions.' },
    { id: 'portfolio', title: 'Portfolio / Projects', description: 'Showcases examples of your past work or projects.', details: 'Include visuals, client testimonials, and measurable results to demonstrate credibility and expertise.' },
    { id: 'careers', title: 'Careers', description: 'Lists current job openings, internships, or volunteer opportunities.', details: 'Share company culture, benefits, and clear instructions for applying.' },
    { id: 'bookings', title: 'Bookings', description: 'Allows clients to schedule appointments, reserve services, or book events.', details: 'Provide available slots, booking forms, and confirmation instructions.' },
    { id: 'blog', title: 'Blog', description: 'A space to share articles, insights, and updates.', details: 'Position your business as an industry authority while engaging your audience with valuable content.' },
    { id: 'faq', title: 'FAQs', description: 'Answers to frequently asked questions about your business.', details: 'Provide clarity on products, services, policies, or processes to reduce customer support workload.' },
    { id: 'landing', title: 'Landing Page', description: 'A standalone page for a campaign, promotion, or specific product.', details: 'Focus on one call-to-action, such as sign-up, purchase, or download.' },
    { id: 'contact', title: 'Contact Us', description: 'Ways for visitors to reach you directly.', details: 'Include enquiry forms, phone numbers, email, physical address, and social media links.' }
  ];

  const additionalPages = [
    { id: 'story', title: 'Our Story', description: 'Shares the journey and evolution of your business.', details: 'Include milestones, origins, challenges, and achievements that connect visitors with your brand.' },
    { id: 'shop', title: 'Shop', description: 'An online store showcasing your products.', details: 'Provide product images, descriptions, prices, categories, and options for purchase.' },
    { id: 'store', title: 'Store', description: 'Details your physical locations and points of sale.', details: 'Include addresses, opening hours, contact information, and maps for easy visitor access.' },
    { id: 'plans', title: 'Plans', description: 'Outlines subscription, service, or product plans available.', details: 'Highlight features, benefits, and differences to help visitors choose the right plan.' },
    { id: 'features', title: 'Features', description: 'Highlights key features of your products or services.', details: 'Focus on benefits and value propositions that make your offerings unique.' },
    { id: 'packages', title: 'Packages', description: 'Grouped offerings tailored to specific client needs.', details: 'Explain what each package includes and why it is ideal for different customer types.' },
    { id: 'reservations', title: 'Reservations', description: 'Allows visitors to reserve tables, rooms, or event spaces.', details: 'Provide available dates, booking forms, and confirmation details.' },
    { id: 'appointments', title: 'Appointments', description: 'Schedule meetings, consultations, or professional services.', details: 'Include available time slots, instructions, and booking confirmation.' },
    { id: 'menu', title: 'Menu', description: 'Displays food, drink, or service options.', details: 'Include descriptions, pricing, categories, and high-quality images.' },
    { id: 'case-studies', title: 'Case Studies', description: 'Showcases detailed examples of completed projects.', details: 'Include challenges, solutions, visuals, and measurable results to demonstrate expertise.' },
    { id: 'success-stories', title: 'Success Stories', description: 'Highlights client or customer achievements.', details: 'Share testimonials, outcomes, and stories of positive impact from your services.' },
    { id: 'insights', title: 'Insights', description: 'Thought leadership content and professional commentary.', details: 'Share market perspectives, advice, and industry trends to engage visitors.' },
    { id: 'articles', title: 'Articles', description: 'Written pieces offering guidance or updates relevant to your audience.', details: 'Focus on practical tips, news, or educational content that positions your business as an authority.' },
    { id: 'knowledge-base', title: 'Knowledge Base', description: 'A self-service hub for guides and documentation.', details: 'Include step-by-step instructions, troubleshooting, FAQs, and helpful resources.' },
    { id: 'news', title: 'News & Announcements', description: 'Updates about your company, milestones, or initiatives.', details: 'Keep visitors informed about important developments and achievements.' },
    { id: 'press', title: 'Press Releases', description: 'Official statements distributed to media.', details: 'Highlight noteworthy events, launches, or corporate communications.' },
    { id: 'events', title: 'Events', description: 'Information about upcoming or past events.', details: 'Include dates, descriptions, schedules, and registration or ticketing links.' },
    { id: 'programmes', title: 'Programmes', description: 'Structured initiatives or projects your business runs.', details: 'Provide details, objectives, target audience, and participation instructions.' },
    { id: 'courses', title: 'Courses', description: 'Educational offerings for clients or the public.', details: 'Include course descriptions, modules, schedules, and enrolment information.' },
    { id: 'destinations', title: 'Destinations', description: 'Promotes travel or location-specific offerings.', details: 'Include imagery, highlights, and key information to attract visitors.' },
    { id: 'tours', title: 'Tours', description: 'Specific travel experiences or guided activities.', details: 'Provide itineraries, schedules, pricing, and booking options.' },
    { id: 'resources', title: 'Resources', description: 'Downloadable guides, templates, or materials.', details: 'Offer practical tools, reports, and helpful content to assist visitors.' },
    { id: 'help', title: 'Help Centre', description: 'Support hub for customer assistance.', details: 'Include FAQs, guides, troubleshooting tips, and contact options.' },
    { id: 'profile', title: 'Profile', description: 'User account management page.', details: 'Allow visitors to update personal details, preferences, and settings.' },
    { id: 'donations', title: 'Donations', description: 'Enable online contributions to support your cause.', details: 'Provide secure donation options, suggested amounts, and instructions.' },
    { id: 'volunteer', title: 'Volunteer', description: 'Information and sign-up for volunteering opportunities.', details: 'Include roles, schedules, benefits, and registration forms.' },
    { id: 'get-involved', title: 'Get Involved', description: 'Ways for stakeholders to engage with your business or cause.', details: 'Include partnerships, memberships, advocacy, or campaign opportunities.' },
    { id: 'privacy', title: 'Privacy Policy (Generic)', description: 'Explains how user data is collected, used, and protected.', details: 'Provide transparency and build trust with clear privacy practices.' },
    { id: 'terms', title: 'Terms & Conditions', description: 'Rules for using your website, products, or services.', details: 'Include user responsibilities, limitations, and disclaimers.' },
    { id: 'tos', title: 'Terms of Service (Generic)', description: 'Formal agreement outlining service use.', details: 'Specify rights, obligations, and legal protections for users.' },
    { id: 'coming-soon', title: 'Coming Soon', description: 'A teaser page for a product, service, or website launch.', details: 'Include visuals, launch date, and call-to-action to capture interest.' },
    { id: 'maintenance', title: 'Maintenance Page', description: 'Displayed during temporary website updates.', details: 'Provide a clear notice and expected time for service restoration.' },
    { id: 'construction', title: 'Under Construction Page', description: 'Placeholder while a page or site is being developed.', details: 'Indicate that content will be available soon and encourage visitors to return.' },
    { id: '404', title: '404 Not Found', description: 'Error page for unavailable content.', details: 'Guide users back to the homepage or other relevant sections with friendly messaging.' }
  ];

  const renderPageSection = (page) => {
    const isSelected = (stepData.selectedPages || []).includes(page.id);
    const isExpanded = expandedPages[page.id];
    const content = stepData.pageContent?.[page.id] || '';

    return (
      <div key={page.id} className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handlePageToggle(page.id)}
              className="agreement-checkbox mt-1"
            />
            <div className="flex-1">
              <h4 className="font-base text-gray-900">{page.title}</h4>
              <p className="text-16px text-[#008080] mt-1">{page.description}</p>
              <p className="text-15px text-gray-500 mt-1">{page.details}</p>
            </div>
          </div>
          {isSelected && (
  <button
    onClick={() => toggleContentSection(page.id)}
    className="text-[#FF5841] text-16px font-medium hover:text-[#E5503A] transition-colors duration-200 focus:outline-none focus:ring-0"
  >
    {isExpanded ? 'Hide' : 'Add content'}
  </button>
)}
        </div>
        {isSelected && isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-16px font-medium text-gray-900 mb-2">
              Content brief
            </label>
            <textarea
              value={content}
              onChange={(e) => handleContentUpdate(page.id, e.target.value)}
              placeholder="Provide the content for this page. You can describe what should appear or paste the full text if available."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended length: 150-300 words</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <StepLayout
      title="Let's map out your digital home! 🏠"
      stepNumber={4}
      totalSteps={7}
      nextButtonText="Define my goals"
      nextStep={5}
      prevStep={3}
    >
      
      <div className="p-4 bg-[#FFF7ED] border-l-4 border-[#F59E0B] rounded-lg mb-8">
        <p className="text-[#92400D] leading-relaxed">
          This is where we plan the structure of your online space together. Think about the journey you want your visitors to take and the stories you want to tell. We will help you create a layout that feels natural and engaging for everyone who visits.
        </p>
      </div>

      <div className="space-y-8">
        {/* Industry Suggestions */}
        {industry && industry !== 'other' && (
          <div className="p-4 bg-[#E5F9F9] rounded-lg">
            <h3 className="font-medium text-[#008080] mb-3">
              Suggested for {formData.step1.industry?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase())}
            </h3>
            <div className="flex flex-wrap gap-2">
              {getIndustrySuggestions().map((suggestion, index) => (
                <span key={index} className="px-3 py-1 bg-[#B2E4E5] text-[#008080] text-sm rounded-full">
                  {suggestion}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Essential Pages */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-6">Select pages for your website</h3>
          <div className="space-y-4">
            {essentialPages.map(renderPageSection)}
          </div>
        </div>

        {/* View All Pages Toggle */}
        <div className="text-center">
        <button
        onClick={() => setShowAllPages(!showAllPages)}
        className="text-[#FF5841] font-medium hover:text-[#E5503A] transition-colors duration-200 focus:outline-none focus:ring-0"
        >
          {showAllPages ? 'Hide additional pages' : 'View all pages'}
        </button>
        </div>

        {/* Additional Pages */}
        {showAllPages && (
          <div>
            <h3 className="text-xl font-medium text-gray-900 mb-6">Additional pages</h3>
            <div className="space-y-4">
              {additionalPages.map(renderPageSection)}
            </div>
          </div>
        )}

        {/* Media Requirements */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">Media requirements</h3>
          <div>
            <label className="block text-16px font-medium text-gray-900 mb-2">
              Image preferences (Optional)
            </label>
            <textarea
              value={stepData.imagePreferences || ''}
              onChange={(e) => updateFormData('step4', { imagePreferences: e.target.value })}
              placeholder="Please describe any preferences for images to be used on your website, for example: People: Age range, professional attire, diversity in appearance, lifestyle, or activities. Environments: Offices, construction sites, nature, urban settings, or other relevant locations. Objects or Themes: Laptops, vehicles, tools, technology, wellness, or other items that reflect your brand."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
            />
            <p className="text-sm text-gray-600 mt-2">
              Providing guidance on imagery helps us select visuals that match your brand identity and appeal to your target audience.
            </p>
          </div>
        </div>
      </div>
    </StepLayout>
  );
}

export default Step4WebsiteContent;